import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { analytics, EVENTS } from "../analytics";
import { useExchangeRate, toKRWLabel } from "../hooks/useExchangeRate";
import { SERVICE_PRICES } from "../constant";

interface Tier {
  badge: string;
  name: string;
  id: string;
  description: string;
  features: string[];
  cta: string;
  path: string;
  highlight: boolean;
}


const HomeServiceTiersSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rate } = useExchangeRate();

  const s = t("serviceTiersSection", { returnObjects: true }) as {
    title: string;
    subtitle: string;
    tiers: Tier[];
  };

  return (
    <section className="py-20 bg-sub">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-main mb-3">{s.title}</h2>
          <p className="text-gray-500">{s.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {s.tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative rounded-2xl p-8 flex flex-col ${
                tier.highlight
                  ? "bg-main text-white shadow-xl"
                  : "bg-white border border-gray-200 shadow-sm"
              }`}
            >
              {tier.badge && (
                <span
                  className={`absolute -top-3 left-6 text-xs font-bold px-3 py-1 rounded-full ${
                    tier.highlight ? "bg-accent text-main" : "bg-main text-white"
                  }`}
                >
                  {tier.badge}
                </span>
              )}

              <div className="mb-6">
                <p className={`text-sm font-medium mb-1 ${tier.highlight ? "text-white/60" : "text-gray-400"}`}>
                  {tier.name}
                </p>
                <p className={`text-4xl font-extrabold ${tier.highlight ? "text-accent" : "text-main"}`}>
                  £{SERVICE_PRICES[tier.id]}
                </p>
                {rate && SERVICE_PRICES[tier.id] && (
                  <p className={`text-sm mt-0.5 ${tier.highlight ? "text-white/50" : "text-gray-400"}`}>
                    {toKRWLabel(SERVICE_PRICES[tier.id] * rate)}
                  </p>
                )}
                <p className={`mt-3 text-sm leading-relaxed ${tier.highlight ? "text-white/75" : "text-gray-500"}`}>
                  {tier.description}
                </p>
              </div>

              <ul className="space-y-2 mb-8 flex-1">
                {tier.features.map((f, fi) => (
                  <li key={fi} className={`flex items-start gap-2 text-sm ${tier.highlight ? "text-white/90" : "text-gray-700"}`}>
                    <Check
                      size={15}
                      className={`mt-0.5 flex-shrink-0 ${tier.highlight ? "text-accent" : "text-main"}`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  navigate(tier.path);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  analytics.track(EVENTS.CLICK_HOME_TIER_TO_APPLY, { tier_name: tier.name });
                }}
                className={`w-full py-3 rounded-xl font-bold transition-colors ${
                  tier.highlight
                    ? "bg-accent text-main hover:bg-yellow-500"
                    : "bg-main text-white hover:opacity-90"
                }`}
              >
                {tier.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeServiceTiersSection;
